import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import type { i18n as I18nInstance } from 'i18next';
import NextGeoDataApi from '@features/api/nextApiService/nextGeoDataApi';
import Button from '@components/Button/Button';
import styles from '@components/LangPopup/LangPopup.styles';
import Icon from '@components/icons/Icon';
import sanitizeHtml from '@utils/sanitizeHtml';
import { useSIteTypesStore } from '@features/footer/components/LanguageSelect/mobX/context/SiteTypesProvider';
import browserStorage from '@utils/browserStorage';
import { useOffer } from '@hooks/useOffer';
import useCountry from '@hooks/useCountry';
import { ENVIRONMENT } from '@const/index';
import { OFFER_TO_REGION_MAP } from '@config/index';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { ISiteTypesData, ISiteTypesDataLanguage, TRegionsEnviroments } from '@typescript/index';
let getGeoData = false;
const DEFAULT_POPUP_LANG_CODE = 'en-EN';
const LANG_POPUP_LOCALE_MAP: Record<string, string> = {
  en: 'en-EN',
  pl: 'pl-PL',
  de: 'de-DE',
  fr: 'fr-FR',
  bg: 'bg-BG',
  ro: 'ro-RO'
};
type PopupState = {
  langCode: string | null;
  defaultLangCode: string | null;
  langUrl: string | null;
  shouldShow: boolean;
};
const EMPTY_POPUP_STATE: PopupState = {
  langCode: null,
  defaultLangCode: null,
  langUrl: null,
  shouldShow: false
};
export type PropsType = {
  active: boolean;
  setActive: (active: boolean) => void;
  promotionalMessagesHeight: number;
};
const getLanguageUrl = (language: ISiteTypesDataLanguage, siteType: ISiteTypesData): string | null => language.urls[ENVIRONMENT as TRegionsEnviroments] || siteType.urls[ENVIRONMENT as TRegionsEnviroments] || language.urls.prod || language.urls.test || language.urls.staging || null;
const toLangPopupLocaleCode = (code: string | null): string | null => {
  if (!code) return null;
  const normalized = code.replace(/_/g, '-').toLowerCase();
  const [lang, region] = normalized.split('-');
  if (LANG_POPUP_LOCALE_MAP[lang]) return LANG_POPUP_LOCALE_MAP[lang];
  return region ? `${lang}-${region.toUpperCase()}` : `${lang}-${lang.toUpperCase()}`;
};
const matchesLangCode = (languageCode: string, newLangCode: string): boolean => {
  const normalizedLanguageCode = languageCode.replace(/_/g, '-').toLowerCase();
  const normalizedNewLangCode = newLangCode.replace(/_/g, '-').toLowerCase();
  if (normalizedLanguageCode === normalizedNewLangCode) return true;
  const [lang, region] = normalizedLanguageCode.split('-');
  return Boolean(normalizedNewLangCode && (lang === normalizedNewLangCode || region === normalizedNewLangCode || normalizedNewLangCode.startsWith(`${lang}-`)));
};
const findLangCode = (siteTypes: ISiteTypesData[] | null, newLangCode: string): string | null => {
  if (!siteTypes) return null;
  let code: string | null = null;
  siteTypes.forEach(siteType => {
    if (code || !siteType?.languages) return;
    siteType.languages.forEach(language => {
      if (code || !language?.code) return;
      if (matchesLangCode(language.code, newLangCode)) {
        code = language.code.replace('_', '-');
      }
    });
  });
  return code;
};
const findLangUrl = (siteTypes: ISiteTypesData[] | null, newLangCode: string): string | null => {
  if (!siteTypes) return null;
  let url: string | null = null;
  siteTypes.forEach(siteType => {
    if (url || !siteType?.languages) return;
    siteType.languages.forEach(language => {
      if (url || !language?.code) return;
      if (matchesLangCode(language.code, newLangCode)) {
        url = getLanguageUrl(language, siteType);
      }
    });
  });
  return url;
};
const hasPopupTranslations = (i18n: I18nInstance, popupLangCode: string, popupDefaultLangCode: string | null): boolean => Boolean(i18n.exists(`langPopup.switchButtonText_${popupLangCode}`) && i18n.exists(`langPopup.selectButtonText_${popupLangCode}`) && popupDefaultLangCode && i18n.exists(`langPopup.country_${popupLangCode}_${popupDefaultLangCode}`));
const resolvePopupData = (siteTypes: ISiteTypesData[] | null, countryCode: string, locale: string, i18n: I18nInstance): PopupState => {
  let langCode = toLangPopupLocaleCode(findLangCode(siteTypes, countryCode.toLowerCase()));
  if (!langCode || !i18n.exists(`langPopup.switchButtonText_${langCode}`)) {
    langCode = DEFAULT_POPUP_LANG_CODE;
  }
  const defaultLangCode = toLangPopupLocaleCode(findLangCode(siteTypes, locale));
  const langUrl = langCode ? findLangUrl(siteTypes, langCode) : null;
  const shouldShow = Boolean(langUrl) && Boolean(defaultLangCode) && defaultLangCode !== langCode && hasPopupTranslations(i18n, langCode, defaultLangCode);
  return {
    langCode,
    defaultLangCode,
    langUrl,
    shouldShow
  };
};
const LangPopup = ({
  active,
  setActive,
  promotionalMessagesHeight
}: PropsType) => {
  const [popupState, setPopupState] = useState<PopupState>(EMPTY_POPUP_STATE);
  const {
    langCode,
    defaultLangCode,
    langUrl
  } = popupState;
  const {
    t,
    i18n
  } = useTranslation('common');
  const siteTypesStore = useSIteTypesStore();
  const popupRef = useRef<HTMLDivElement>(null);
  const {
    codeToName
  } = useCountry();
  const [offer] = useOffer();
  const {
    langPopupHeight,
    setLangPopupHeight,
    barPositionStatic
  } = layoutUiState;
  const onResize = () => {
    if (popupRef?.current?.clientHeight && active) {
      const newPopupHeight = popupRef.current?.clientHeight;
      if (+newPopupHeight !== (langPopupHeight ? +langPopupHeight : 0)) {
        setLangPopupHeight(+newPopupHeight);
      }
    }
  };
  const init = async () => {
    getGeoData = true;
    const geoData = await NextGeoDataApi.getGeoData();
    if (!geoData) return;
    const {
      userGeolocation,
      locale
    } = geoData;
    if (!userGeolocation?.country || !siteTypesStore.siteTypes) return;
    const resolved = resolvePopupData(siteTypesStore.siteTypes, userGeolocation.country, locale, i18n);
    setPopupState(resolved);
    if (!resolved.shouldShow) return;
    setTimeout(() => {
      setActive(true);
      onResize();
    }, 1);
  };
  const getMessage = () => {
    const countryCode = OFFER_TO_REGION_MAP[offer];
    const lang = langCode ? langCode.split('-')[0] : undefined;
    let countryName = codeToName(countryCode, lang);
    if (!countryName) {
      countryName = countryCode;
    }
    const message = langCode ? t(`langPopup.message_${langCode}`, {
      country: countryName.toUpperCase()
    }) : '';
    return sanitizeHtml(message || '');
  };
  const getSwitchButtonText = () => {
    const switchButtonText = langCode ? t(`langPopup.switchButtonText_${langCode}`) : '';
    return switchButtonText || '';
  };
  const getSelectButtonText = () => {
    const selectButtonText = langCode ? t(`langPopup.selectButtonText_${langCode}`) : '';
    return selectButtonText || '';
  };
  const setLangPopupClose = () => browserStorage ? browserStorage.setItem(`langPopupClose`, 'true') : null;
  const getLangPopupClose = () => browserStorage ? browserStorage.getItem(`langPopupClose`) : null;
  const closeLangPopup = () => {
    setLangPopupClose();
    setActive(false);
    setLangPopupHeight(0);
  };
  useEffect(() => {
    if (!getGeoData && !getLangPopupClose()) {
      (async () => {
        await siteTypesStore.initializeData();
        init();
      })();
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [active, langPopupHeight]);
  const canRenderPopup = getGeoData && langCode && defaultLangCode && langUrl;
  return <>
      {canRenderPopup && <div sx={styles.container(active, promotionalMessagesHeight, barPositionStatic)}>
          <div sx={styles.popup(active)} ref={popupRef}>
            <span sx={styles.message} dangerouslySetInnerHTML={getMessage()} />
            <div sx={styles.buttons}>
              <Button variant="outlined" onClick={() => window.open(langUrl, '_self')} variantColor="white" size="small">
                {getSwitchButtonText()}
              </Button>
              <Button variant="link" variantColor="white" size="micro" onClick={() => siteTypesStore.openSidebar(langCode)}>
                {getSelectButtonText()}
              </Button>
              <Button addSx={styles.buttons.close} variant="iconOnly" variantColor="white" onClick={closeLangPopup} cy="GoToGlobalCloseButton">
                <Icon icon="Close3" size="24px" />
              </Button>
            </div>
          </div>
        </div>}
    </>;
};
export default observer(LangPopup);